import { useCallback, useEffect, useState } from "react";
import { Bell, X, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Universal 🔔 Set Reminder dialog (owner spec §6-§8): date/time/timezone,
   in-app + email channels, smart presets, multiple reminders per item. */

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const presetsFor = (kind, refDate) => {
  const base = refDate ? new Date(refDate + "T09:00:00") : null;
  const mk = (label, days, hours = 0) => base ? { label, at: new Date(base.getTime() - days * 864e5 - hours * 36e5) } : null;
  if (kind === "trip" && base) {
    return [mk("1 month before", 30), mk("2 weeks before", 14), mk("1 week before", 7), mk("1 day before", 1)].filter((p) => p && p.at > new Date());
  }
  const now = new Date();
  return [
    { label: "In 1 hour", at: new Date(now.getTime() + 36e5) },
    { label: "Tomorrow 9 AM", at: new Date(new Date(now.getTime() + 864e5).setHours(9, 0, 0, 0)) },
    { label: "In 1 week", at: new Date(now.getTime() + 7 * 864e5) },
  ];
};

export default function ReminderDialog({ entityType = "trip", entityId, tripId, defaultTitle, refDate, onClose, onSaved }) {
  const [title, setTitle] = useState(defaultTitle || "");
  const [message, setMessage] = useState("");
  const [when, setWhen] = useState(toLocalInput(new Date(Date.now() + 36e5)));
  const [inApp, setInApp] = useState(true);
  const [email, setEmail] = useState(true);
  const [existing, setExisting] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() =>
    api.get(`/reminders/entity/${entityType}/${entityId}`).then((r) => setExisting(r.data.items)).catch(() => {}),
    [entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!title.trim()) { toast.error("Give the reminder a title"); return; }
    setBusy(true);
    try {
      await api.post("/reminders", {
        entity_type: entityType, entity_id: entityId, trip_id: tripId || null,
        title, message, remind_at: new Date(when).toISOString(), timezone: TZ,
        in_app: inApp, email,
      });
      toast.success("Reminder set");
      setMessage("");
      load();
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set reminder"); }
    finally { setBusy(false); }
  };

  const cancel = async (rid) => {
    try { await api.delete(`/reminders/${rid}`); load(); onSaved?.(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="reminder-dialog" onClick={onClose}>
      <div className="bg-white border border-zinc-300 shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between bg-indigo-700 text-white px-4 py-3">
          <div className="font-heading font-900 flex items-center gap-2"><Bell className="w-5 h-5" /> Set Reminder</div>
          <button onClick={onClose} data-testid="reminder-close" className="hover:bg-white/20 p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block text-sm"><span className="font-700">Remind me about</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="reminder-title-input"
              className="w-full border border-zinc-300 px-3 py-2 mt-1 text-sm outline-none focus:ring-2 focus:ring-primary" /></label>
          <label className="block text-sm"><span className="font-700">Note <span className="font-400 text-muted-foreground">(optional)</span></span>
            <input value={message} onChange={(e) => setMessage(e.target.value)} data-testid="reminder-message-input" placeholder="Check in online, pack chargers…"
              className="w-full border border-zinc-300 px-3 py-2 mt-1 text-sm outline-none focus:ring-2 focus:ring-primary" /></label>

          <div className="flex flex-wrap gap-1.5">
            {presetsFor(refDate ? "trip" : "generic", refDate).map((p) => (
              <button key={p.label} onClick={() => setWhen(toLocalInput(p.at))} data-testid={`reminder-preset-${p.label.replace(/\s+/g, "-").toLowerCase()}`}
                className="inline-flex items-center gap-1 text-xs border border-indigo-300 text-indigo-700 px-2 py-1 hover:bg-indigo-50">
                <Zap className="w-3 h-3" /> {p.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm"><span className="font-700 block">Date &amp; time</span>
              <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="reminder-when-input"
                className="border border-zinc-300 px-3 py-2 mt-1 text-sm bg-white" /></label>
            <span className="text-xs text-muted-foreground pb-2">Timezone: {TZ}</span>
          </div>

          <div className="flex gap-4 text-sm">
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={inApp} onChange={(e) => setInApp(e.target.checked)} data-testid="reminder-inapp-check" /> In-app notification</label>
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} data-testid="reminder-email-check" /> Email</label>
          </div>

          <button onClick={save} disabled={busy} data-testid="reminder-save-btn"
            className="w-full bg-indigo-700 text-white font-700 py-2.5 text-sm hover:bg-indigo-600 disabled:opacity-50">
            {busy ? "Saving…" : "Set reminder"}
          </button>

          {existing.length > 0 && (
            <div className="border-t border-zinc-200 pt-3">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Reminders on this item</div>
              <div className="space-y-1.5">
                {existing.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-sm" data-testid={`reminder-row-${r.id}`}>
                    <Bell className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                    <span className="flex-1 truncate">{r.title}</span>
                    <span className="text-xs text-muted-foreground">{new Date(r.remind_at).toLocaleString()}</span>
                    <span className={`text-[11px] px-1.5 py-0.5 border capitalize ${r.status === "completed" ? "bg-zinc-50 text-zinc-500" : r.status === "sent" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"}`}>{r.status}</span>
                    {["scheduled", "snoozed"].includes(r.status) && (
                      <button onClick={() => cancel(r.id)} data-testid={`reminder-cancel-${r.id}`} className="text-muted-foreground hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
